/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';

import {
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';
import TopbarMobileMenuHeader from './TopbarMobileMenuHeader';

const isBecomeSellerLink = linkConfig => {
  const pageId = linkConfig?.route?.params?.pageId;
  const href = linkConfig?.href;
  return pageId === 'become-seller' || href === '/p/become-seller';
};

const CustomLinkComponent = ({ linkConfig, currentPage, onCloseMenu }) => {
  const { text, type, href, route } = linkConfig;

  const getIsActive = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || currentPage === page;
  };

  const linkClassName = classNames(css.navLink, getIsActive(route?.name) && css.navLinkActive);

  if (type === 'internal' && route) {
    const { name, params, to } = route || {};
    return (
      <li className={css.navItem}>
        <NamedLink
          name={name}
          params={params}
          to={to}
          className={linkClassName}
          onClick={onCloseMenu}
        >
          {text}
        </NamedLink>
      </li>
    );
  }
  return (
    <li className={css.navItem}>
      <ExternalLink href={href} className={css.navLink} onClick={onCloseMenu}>
        {text}
      </ExternalLink>
    </li>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @param {Function} props.onCloseMenu
 * @param {Function} props.onOpenSearch
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showBecomeSellerLink
 * @param {string} props.menuCloseButtonId
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUser,
    inboxTab,
    notificationCount = 0,
    customLinks,
    onLogout,
    onCloseMenu,
    onOpenSearch,
    showSearchForm,
    showCreateListingsLink,
    showBecomeSellerLink = true,
    menuCloseButtonId,
  } = props;

  const menuHeader = (
    <TopbarMobileMenuHeader
      isAuthenticated={isAuthenticated}
      currentUser={currentUser}
      currentPage={currentPage}
      notificationCount={notificationCount}
      inboxTab={inboxTab}
      showSearchForm={showSearchForm}
      showCreateListingsLink={showCreateListingsLink}
      onCloseMenu={onCloseMenu}
      onOpenSearch={onOpenSearch}
      onLogout={onLogout}
      menuCloseButtonId={menuCloseButtonId}
    />
  );

  const linksForNav = showBecomeSellerLink
    ? customLinks.filter(linkConfig => !isBecomeSellerLink(linkConfig))
    : customLinks;

  const extraLinks = linksForNav.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
        onCloseMenu={onCloseMenu}
      />
    );
  });

  const becomeSellerLinkMaybe = showBecomeSellerLink ? (
    <li className={css.navItem}>
      <NamedLink
        name="CMSPage"
        params={{ pageId: 'become-seller' }}
        className={classNames(
          css.navLink,
          currentPage === 'CMSPage:become-seller' && css.navLinkActive
        )}
        onClick={onCloseMenu}
      >
        <FormattedMessage id="TopbarMobileMenu.becomeASellerLink" />
      </NamedLink>
    </li>
  ) : null;

  const createListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink className={css.createNewListingLink} name="NewListingPage" onClick={onCloseMenu}>
      <FormattedMessage id="TopbarMobileMenu.newListingLink" />
    </NamedLink>
  ) : null;

  const isCurrentPage = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage =
      currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage;
  };

  const navLinkClass = page => classNames(css.navLink, isCurrentPage(page) && css.navLinkActive);

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink} onClick={onCloseMenu}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink} onClick={onCloseMenu}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage
          id="TopbarMobileMenu.signupOrLogin"
          values={{ lineBreak: <br />, signup, login }}
        />
      </span>
    );

    const signupCta = (
      <NamedLink className={css.createNewListingLink} name="SignupPage" onClick={onCloseMenu}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    return (
      <nav className={css.root}>
        {menuHeader}
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          <ul className={css.navList}>
            {becomeSellerLinkMaybe}
            {extraLinks}
          </ul>
        </div>
        <div className={css.footer}>{signupCta}</div>
      </nav>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const manageListingsLinkMaybe = showCreateListingsLink ? (
    <li className={css.navItem}>
      <NamedLink
        name="ManageListingsPage"
        className={navLinkClass('ManageListingsPage')}
        onClick={onCloseMenu}
      >
        <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
      </NamedLink>
    </li>
  ) : null;

  return (
    <nav className={css.root}>
      {menuHeader}
      <div className={css.content}>
        <ul className={css.navList}>
          {becomeSellerLinkMaybe}
          {manageListingsLinkMaybe}
          <li className={css.navItem}>
            <NamedLink
              name="InboxPage"
              params={{ tab: inboxTab }}
              className={classNames(navLinkClass(`InboxPage:${inboxTab}`), css.inboxLink)}
              onClick={onCloseMenu}
            >
              <FormattedMessage id="TopbarMobileMenu.inboxLink" />
              {notificationCountBadge}
            </NamedLink>
          </li>
          <li className={css.navItem}>
            <NamedLink
              name="ProfileSettingsPage"
              className={navLinkClass('ProfileSettingsPage')}
              onClick={onCloseMenu}
            >
              <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
            </NamedLink>
          </li>
          <li className={css.navItem}>
            <NamedLink
              name="AccountSettingsPage"
              className={navLinkClass('AccountSettingsPage')}
              onClick={onCloseMenu}
            >
              <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
            </NamedLink>
          </li>
          {extraLinks}
          <li className={classNames(css.navItem, css.logoutItem)}>
            <InlineTextButton rootClassName={css.logoutLink} onClick={onLogout}>
              <FormattedMessage id="TopbarMobileMenu.logoutLink" />
            </InlineTextButton>
          </li>
        </ul>
      </div>
      <div className={css.footer}>{createListingsLinkMaybe}</div>
    </nav>
  );
};

export default TopbarMobileMenu;
